import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const Branch = () => {
  const [formData, setFormData] = useState({
    latitude: '',
    longitude: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showAlert, setShowAlert] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(null);

  const [mapInstance, setMapInstance] = useState(null);
  const [markerInstance, setMarkerInstance] = useState(null);

  const getCompanyId = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.companyId || decoded.company?._id || decoded.orgId || decoded.id;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  // Fetch company details on mount
  const fetchCompanyDetails = async () => {
    const companyId = getCompanyId();
    const token = localStorage.getItem('authToken');
    if (!companyId || !token) {
      setError('Session expired. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://greenyellow-nightingale-241896.hostingersite.com/api/companies/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const company = response.data;
      if (company) {
        setFormData({
          latitude: company.latitude || '',
          longitude: company.longitude || ''
        });
      }
    } catch (err) {
      console.error('Error fetching company details:', err);
      setError('Failed to load company details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
  }, []);

  // Dynamically load Leaflet resources
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (!document.getElementById('leaflet-js')) {
      const script = document.createElement('script');
      script.id = 'leaflet-js';
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Initialize Map
  useEffect(() => {
    if (loading) return;

    const timer = setTimeout(() => {
      const mapDiv = document.getElementById('map');
      if (!mapDiv || mapInstance) return;

      if (typeof window.L === 'undefined') {
        console.error('Leaflet is not loaded yet');
        return;
      }

      const L = window.L;

      // Fix default marker icon paths in webpack/React builds
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      const defaultLat = parseFloat(formData.latitude) || 19.0760;
      const defaultLng = parseFloat(formData.longitude) || 72.8777;

      const map = L.map('map').setView([defaultLat, defaultLng], 13);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(map);

      const marker = L.marker([defaultLat, defaultLng], { draggable: true }).addTo(map);

      marker.on('dragend', function () {
        const position = marker.getLatLng();
        setFormData({
          latitude: position.lat.toFixed(6),
          longitude: position.lng.toFixed(6)
        });
      });

      map.on('click', function (event) {
        const { lat, lng } = event.latlng;
        marker.setLatLng([lat, lng]);
        setFormData({
          latitude: lat.toFixed(6),
          longitude: lng.toFixed(6)
        });
      });

      setMapInstance(map);
      setMarkerInstance(marker);

      setTimeout(() => {
        map.invalidateSize();
      }, 300);
    }, 500);

    return () => clearTimeout(timer);
  }, [loading, mapInstance]);

  // Sync inputs to map
  useEffect(() => {
    if (mapInstance && markerInstance) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        const currentLatLng = markerInstance.getLatLng();
        if (Math.abs(currentLatLng.lat - lat) > 0.0001 || Math.abs(currentLatLng.lng - lng) > 0.0001) {
          markerInstance.setLatLng([lat, lng]);
          mapInstance.setView([lat, lng]);
        }
      }
    }
  }, [formData.latitude, formData.longitude]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveLocation = async (e) => {
    e.preventDefault();
    const companyId = getCompanyId();
    const token = localStorage.getItem('authToken');
    if (!companyId || !token) {
      setShowAlert('❌ Session expired. Please log in.');
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      setShowAlert('❌ Please select a location on the map first.');
      return;
    }

    try {
      setSubmitting(true);
      const formDataToSend = new FormData();
      formDataToSend.append('latitude', formData.latitude);
      formDataToSend.append('longitude', formData.longitude);

      await axios.put(
        `https://greenyellow-nightingale-241896.hostingersite.com/api/companies/${companyId}`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      setShowSuccessAlert('✅ Company Location saved successfully!');
      setTimeout(() => setShowSuccessAlert(null), 3000);
    } catch (err) {
      console.error('Error saving company location:', err);
      setShowAlert('❌ Failed to save location. Please try again.');
      setTimeout(() => setShowAlert(null), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading location details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded">Refresh</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Main Location Settings Panel */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">📍 Set Company Location</h2>
          <p className="text-sm text-slate-500 mb-6">Select your company's location boundaries on the map to fetch accurate employee attendance tracking.</p>

          <form onSubmit={handleSaveLocation} className="space-y-6">

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

              {/* Left Settings Panel */}
              <div className="lg:col-span-4 space-y-4">

                {/* Search Bar */}
                <div className="flex flex-col gap-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">Search Map</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Search pincode, city, or address..."
                      id="map-search-input"
                      className="flex-1 px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          document.getElementById('map-search-btn')?.click();
                        }
                      }}
                    />
                    <button
                      type="button"
                      id="map-search-btn"
                      onClick={async () => {
                        const query = document.getElementById('map-search-input')?.value;
                        if (!query) return;
                        try {
                          const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=in&q=${encodeURIComponent(query)}`);
                          if (res.ok) {
                            const results = await res.json();
                            if (results && results.length > 0) {
                              const { lat, lon } = results[0];
                              const parsedLat = parseFloat(lat);
                              const parsedLng = parseFloat(lon);

                              setFormData({
                                latitude: parsedLat.toFixed(6),
                                longitude: parsedLng.toFixed(6)
                              });

                              if (mapInstance && markerInstance) {
                                markerInstance.setLatLng([parsedLat, parsedLng]);
                                mapInstance.setView([parsedLat, parsedLng], 14);
                              }
                            } else {
                              alert('Location not found. Please try a different pincode or address.');
                            }
                          }
                        } catch (err) {
                          console.error('Error geocoding:', err);
                        }
                      }}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition"
                    >
                      Locate
                    </button>
                  </div>
                </div>

                {/* Coordinate Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-slate-50 focus:ring-2 focus:ring-indigo-500"
                      placeholder="Latitude"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-slate-50 focus:ring-2 focus:ring-indigo-500"
                      placeholder="Longitude"
                      required
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <div className="bg-indigo-50 rounded-xl p-4 text-xs text-indigo-700 space-y-2">
                    <p className="font-semibold">💡 Tips:</p>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Enter a pincode and click <strong>Locate</strong> to jump to your area.</li>
                      <li>Drag the red map marker or click any point on the map to pin exact coordinates.</li>
                      <li>Click <strong>Save Location</strong> below to store coordinates.</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* Right Column: Visual Map Picker */}
              <div className="lg:col-span-8 flex flex-col min-h-[400px]">
                <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Location Pin Picker</span>
                <div id="map" className="w-full rounded-xl border border-slate-200 shadow-xs flex-1" style={{ minHeight: '400px', zIndex: 1 }}></div>
              </div>

            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-slate-100">
              <button
                type="submit"
                disabled={submitting}
                className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-lg transition font-semibold disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                {submitting ? 'Saving...' : 'Save Location'}
              </button>
            </div>

          </form>
        </div>

      </div>

      {showSuccessAlert && <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 font-semibold">{showSuccessAlert}</div>}
      {showAlert && <div className="fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 font-semibold">{showAlert}</div>}
    </div>
  );
};

export default Branch;
